import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { BottomTab } from './components/SideNav/BottomTab';
import { CoachFabGate } from './components/Fab/CoachFabGate';
import { DraftLandedChip } from './components/plan/DraftLandedChip';
import { RouteFallback } from './components/RouteFrame/RouteFrame';
import { BootVeil } from './components/boot/BootVeil';
import { BootActivityMount } from './components/boot/BootActivityMount';
import { TypeRampRoot } from './components/boot/TypeRampRoot';
import { BootProvider } from './contexts/BootContext';
import { HealthSyncMount } from './components/health/HealthSyncMount';
import { WorkoutImportMount } from './components/health/WorkoutImportMount';
import { RunSaveQueueMount } from './components/run/RunSaveQueueMount';
import { StoreKitSyncMount } from './components/billing/StoreKitSyncMount';
import { NativeLinkGuard } from './components/NativeLinkGuard';
import { SignedOutHold, signedOutBlankRemainingMs } from './components/SignedOutHold';
import { PushTapMount } from './components/push/PushTapMount';
import { JoinCodeMount } from './components/team/JoinCodeMount';
import { TimezoneStampMount } from './components/TimezoneStampMount';
// B76C lane C (handoff 2b + 2e) — the SHEET SIDE of lane B's seam. The host
// provides `AthleteMembershipSheetContext` to the whole route tree and renders
// the one membership sheet; the four boundary CTAs open it by context, never
// by import. The banner is the 2e arrival confirmation, self-gating like
// DraftLandedChip (armed by the crossing, shown once, dismissible).
import { AthleteMembershipHost } from './components/membership/AthleteMembershipHost';
import { DualArrivalBanner } from './components/membership/DualArrivalBanner';
// B76B lane B (handoff 1c) — a coach who lands on ANY athlete surface gets the
// athlete-side boundary card instead. Wrapping the ROUTES (rather than editing
// each surface) is what makes "same card, same two lines, everywhere" true by
// construction, and it keeps every athlete surface byte-identical: on an
// athlete account the guard renders its children with no wrapper element.
// ⚠️ /races is deliberately NOT guarded — a coach uses that slot as "Meets".
import { AthleteSurfaceGuard } from './components/roleSurfaces/AthleteSurfaceBoundary';
import { useAccountView } from './hooks/useAccountView';
import { useServerFlag } from './hooks/useServerFlag';
import { landingPathForView } from './lib/nav/tabSets';
import { parseWeekParam } from './lib/parseWeekParam';
import { isNativeApp, routerBasename } from './lib/platform';
import { NotFound } from './routes/not-found';
import { Onboarding } from './routes/onboarding';
import { Primitives } from './routes/primitives';
import { Today } from './routes/today';
// B495: the styled frame while the Settings chunk loads (no unstyled "Loading settings…" flush top-left).
import { SettingsRouteFallback } from './components/settings/SettingsRouteFallback';

// Marketing landing is lazy-loaded — keeps /v2/today and
// /v2/onboarding bundles lean (existing users don't hit /v2/landing).
//
// B418 fix round 1: the lazy route and the signed-out hold's prefetch share ONE loader, so the
// hold's download is the route's download. A visitor the hold has been keeping blank is on the
// way to this page, so while its chunk is still arriving (or React.lazy takes its first tick on a
// chunk that is in) the fallback stays blank rather than painting "Loading landing page…". Every
// other visit keeps the text.
//
// B430: that blank is part of the hold's 4 s, not on top of it. The fallback keeps blank only for
// what is left of `SIGNED_OUT_HOLD_CAP_MS` since the hold started (`signedOutBlankRemainingMs`),
// then paints its text. Nothing here stays true for the life of the page: a direct /landing visit,
// a signed-in user, and any visit after the 4 s find 0 left and show the text at once.
const loadLanding = () => import('./routes/landing');
const Landing = lazy(loadLanding);
const prefetchLandingForHold = () => loadLanding();

function LandingFallback() {
  const [left] = useState(() => signedOutBlankRemainingMs());
  const [blank, setBlank] = useState(left > 0);
  useEffect(() => {
    if (!blank) return undefined;
    const timer = setTimeout(() => setBlank(false), left);
    return () => clearTimeout(timer);
  }, [blank, left]);
  return blank ? null : <div role="status">Loading landing page…</div>;
}

// Phase 4b PR 4 — chat surface lazy-loaded for two reasons:
//   1. Behind feature flag `react_chat_v2_enabled` (URL param ?chat_v2=1
//      OR localStorage). Most loads won't fetch this bundle.
//   2. Pulls in @supabase/supabase-js + chat components — sizeable
//      enough to deserve its own chunk.
const Chat = lazy(() => import('./routes/chat'));

// Phase 5 PR 5.2 — `/v2/plan` macro-plan view. Lazy-loaded so the
// dashboard bundle stays lean; users only fetch this chunk after
// clicking "View full plan →" or navigating directly.
// Wave 2 Step 0e (spec 4.4e): `/plan` and `/plan/season` enter through
// routes/plan-entry.tsx, which holds the `train_2_0_v1` read and renders the
// same lazy `./routes/plan` as before with the flag off. One chunk serves both.
const PlanEntry = lazy(() => import('./routes/plan-entry').then((m) => ({ default: m.PlanEntry })));
const SeasonEntry = lazy(() => import('./routes/plan-entry').then((m) => ({ default: m.SeasonEntry })));

// Phase 5 PR 5.3 — `/v2/plan/week/:n` weekly drill-down. Lazy-loaded
// alongside the macro-plan view; reuses the shared `plan.module.css`
// + the chat surface's PlanTable component.
const PlanWeek = lazy(() => import('./routes/plan-week'));

// SP-3 — `/v2/plan/first-look` single first-plan review surface. Lazy-loaded;
// entered from onboarding Step 7 when `first_plan_v1` is ON (else the old
// empty-state ladder is the path). Takeover chrome; BottomTab is suppressed via
// CHROME_FREE_PATHS and CoachFab already hides on `/plan/*`.
const FirstLook = lazy(() => import('./routes/plan-first-look'));

// B-3 — `/v2/plan/draft` draft-week journey takeover. Lazy-loaded like the
// other plan takeovers; entered from the re-aimed entry CTAs when
// `draft_week_journey_v1` is ON (else the legacy chat deep-link is the path).
// Takeover chrome; BottomTab is suppressed via CHROME_FREE_PATHS.
const PlanDraft = lazy(() => import('./routes/plan-draft'));

// Backlog J — `/v2/plan/calendar` read-only season grid. Lazy-loaded
// like the other plan views; entered via "Calendar view →" on /v2/plan.
const PlanCalendar = lazy(() => import('./routes/plan-calendar'));

// Phase 6b1 PR 1 — `/v2/settings` route. Lazy-loaded so the dashboard
// bundle stays lean; users only fetch this chunk after navigating to
// Settings (e.g., via the QuickActions "Update context" wire that
// lands in Phase 6b1 PR 8).
const Settings = lazy(() => import('./routes/settings'));

// Phase 6b1 PR 5 — `/v2/races` route. Lazy-loaded for the same
// reason as Settings; users only fetch this chunk after navigating
// to Races (e.g., via the QuickActions "Log a race" wire that lands
// in Phase 6b1 PR 8). Read-only at this PR; race form + write hooks
// (createResult / updateResult / deleteResult) ship in PR 6.
const Races = lazy(() => import('./routes/races'));

// Phase 6b2 PR 1 — `/v2/team` route shell + read-only roster view.
// Lazy-loaded so the dashboard bundle stays lean; users only fetch
// this chunk when navigating to Team via the SideNav (PR 1 activates
// the slot that was placeholder-only since 6b1 PR 8). Buttons render
// disabled in PR 1 — write surface (T2 accept / T6 create / T7
// remove / T8 leave) lands in PR 2.
const Team = lazy(() => import('./routes/team'));

// Admin migration PR-1 — `/v2/admin` React shell (the last vanilla
// surface). Lazy-loaded so the athlete bundle never pays for admin code;
// only admins ever fetch this chunk. Both `/admin` and `/admin/:tab`
// mount the same <Admin> element — it reads the tab segment and gates on
// useAdminGate (signed-out → /landing, non-admin → /today, missing-
// permission → in-place <NoAccess>).
const Admin = lazy(() => import('./routes/admin'));

// CG-4 — `/v2/approvals` guardian approval inbox (the designed v1.1
// surface). Lazy-loaded (most users never guard anyone); reached from
// the approval_request notification, the guardian nav, or by URL.
const Approvals = lazy(() => import('./routes/approvals'));

// CG-4 — `/v2/guardian` guardian home (derived signals only). Lazy-loaded
// like Approvals; guardian-only accounts land here, dual accounts reach it
// via the account menu's "Switch to guardian view".
const GuardianHome = lazy(() => import('./routes/guardian-home'));

// Mobile arc Part A — the live-run spike screen. Registered ONLY inside
// the Capacitor shell or vite DEV (the deployed web app doesn't grow a
// surface from the spike); lazy so maplibre-gl never rides a web chunk.
const RunSpike = lazy(() => import('./routes/run-spike'));

// P3.3 (E4) — the route naming / progress-compare screen. Reached from a
// matched run row (or right after naming a route); flag-gated (route_compare_v1)
// and self-scoped via the API. Review-anywhere: registered on web + shell.
const RouteCompare = lazy(() => import('./routes/route-compare'));

// Map lane step 2 — the coach race-day live map. Dark behind
// race_telemetry_r2; lazy so the MapKit substrate never rides a web chunk
// until a coach actually opens the map.
const RaceLiveMap = lazy(() => import('./routes/race-live-map'));

// B14 — the in-app door: /race-live/today resolves TODAY'S meet for the
// coach's team (redirect / picker / honest empty). Same dark-flag posture.
const RaceLiveToday = lazy(() => import('./routes/race-live-today'));

// Mobile arc Part B — the shell's boot splash + auth-aware entry route.
const NativeRoot = lazy(() => import('./routes/native-root'));

// Mobile v1 D5 — the shell's signed-out welcome (native replaces the
// in-shell web landing). Registered ONLY inside the Capacitor shell;
// the web /landing route and rendering are untouched.
const NativeWelcome = lazy(() => import('./routes/native-welcome'));

/**
 * Per-route document title (UX review 2026-09-08).
 *
 * `index.html` ships `<title>Tayro · Today</title>` and nothing ever wrote
 * `document.title` again, so every tab, every bookmark and every browser
 * history entry read "Tayro · Today" whatever the reader was looking at. One
 * effect keyed on the pathname fixes all of them at once: no route file
 * changes, and `index.html` keeps its default for the first paint.
 *
 * Exact paths, because the pathname is the whole input. `/run` takes its
 * sub-routes too (the replay and the route-compare screen are Runs). Anything
 * unlisted — /landing, /welcome, /onboarding, /admin, /guardian, a 404 — is
 * plain "Tayro" rather than a guess.
 */
const ROUTE_TITLES: Record<string, string> = {
  '/today': 'Today',
  '/plan': 'Season plan',
  '/plan/season': 'Season plan',
  '/plan/calendar': 'Calendar',
  '/plan/draft': 'Draft',
  '/races': 'Races',
  '/team': 'Team',
  // Both paths mount the same <Team /> route below.
  '/team/kudos': 'Team',
  '/settings': 'Settings',
  '/chat': 'Chat',
};

/**
 * The pathname as the ROUTER reads it. `compilePath` (@remix-run/router)
 * compiles every route matcher with the `i` flag and ends it in `\/*$`, so
 * `/PLAN/WEEK/6//` mounts the week route exactly as `/plan/week/6` does. The
 * title read the raw string, which left the invariant below true only for the
 * shapes someone had typed into a test (register pass, 2026-09-09). `/`
 * survives the trim as `/`.
 */
function routerPathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '').toLowerCase();
  return trimmed === '' ? '/' : trimmed;
}

/**
 * The title for a pathname. Exported for the title test. `trainV2` is `train_2_0_v1`: with it on, `/plan` is This
 * week (not the season plan, which moved to `/plan/season`), so the tab says so. Absent or false, every title is
 * what it has always been.
 */
export function documentTitleFor(rawPathname: string, trainV2 = false): string {
  const pathname = routerPathname(rawPathname);
  if (trainV2 && pathname === '/plan') return 'Tayro · This week';
  const named = ROUTE_TITLES[pathname];
  if (named) return `Tayro · ${named}`;
  // /plan/week/:n — the number is the title, read with the SAME parser the
  // week route reads its param with (`lib/parseWeekParam`), off the same
  // string the route sees. That second half is B98 (critic 2 and security L1,
  // 2026-09-09): React Router also matches a trailing slash, and it hands the
  // route a percent-DECODED param, so `/plan/week/6/` and `/plan/week/%36`
  // used to render an h1 "Week 6" under a tab that said "Tayro". The
  // invariant is narrow and now true: for a pathname the week route matches,
  // the tab and the h1 read the same number, or neither names a week. A
  // segment that cannot be decoded ("%ZZ") is not a week.
  const week = /^\/plan\/week\/([^/]+)$/.exec(pathname);
  if (week) {
    let raw: string | null;
    try {
      raw = decodeURIComponent(week[1]);
    } catch {
      raw = null;
    }
    const n = raw === null ? null : parseWeekParam(raw);
    if (n !== null) return `Tayro · Week ${n}`;
  }
  if (pathname === '/run' || pathname.startsWith('/run/')) return 'Tayro · Runs';
  return 'Tayro';
}

/** Renders nothing; writes the title on every navigation. */
export function DocumentTitle() {
  const { pathname } = useLocation();
  // Read above everything, with the id as a literal (the config export rake reads literal call sites).
  const trainV2 = useServerFlag('train_2_0_v1');
  useEffect(() => {
    document.title = documentTitleFor(pathname, trainV2);
  }, [pathname, trainV2]);
  return null;
}

/**
 * 2.0 wave 2, ruling 10 (2026-10-18) - a cold start opens the stored View's
 * first tab.
 *
 * The shell starts at the app root `/`, where `NativeRoot` shows the launch beat
 * and then replace-navigates to `/today`. With `settings_2_0_v1` on, an account
 * whose stored View (`useAccountView`, the chrome's one fact) is Coaching or
 * Guardian lands on that View's first tab (`landingPathForView`) instead of
 * Today, once, in the same replace. The View is trusted only when `ready`, the
 * chrome's own rule: a stored View the account's facts have not confirmed is
 * never acted on.
 *
 * It is a COLD START and nothing else: it arms only when the router began at
 * `/`, and it ends, without moving anyone, on the first of
 *   - any navigation after the hand-off to Today (a tap, a push tap, a redirect),
 *   - landing anywhere but Today (a deep link, `/welcome`, onboarding),
 *   - the account's facts taking longer than `COLD_START_WINDOW_MS` (a reader
 *     already on Today is never moved late),
 *   - the decision itself (one shot).
 * Choosing a View from the switch lands on its first tab on its own
 * (`ViewSwitch`); this is the same destination for the launch.
 *
 * Flag off: the outer component reads the flag and renders null, and the inner
 * one, which is the only reader of `useAccountView`, never mounts. The flag id is
 * a string literal on purpose (the config export rake reads literal call sites).
 */
const COLD_START_WINDOW_MS = 5000;

function ColdStartLandingTo({ onDone }: { onDone: () => void }): null {
  const { view, ready } = useAccountView();
  const location = useLocation();
  const navigate = useNavigate();
  const arrivedKey = useRef(location.key);
  const moved = location.key !== arrivedKey.current;

  useEffect(() => {
    if (moved) onDone();
  }, [moved, onDone]);

  useEffect(() => {
    const t = setTimeout(onDone, COLD_START_WINDOW_MS);
    return () => clearTimeout(t);
  }, [onDone]);

  useEffect(() => {
    if (!ready || moved) return;
    onDone();
    const target = landingPathForView(view);
    if (target !== location.pathname) navigate(target, { replace: true });
  }, [ready, moved, view, location.pathname, navigate, onDone]);

  return null;
}

/** Renders nothing. Exported for the cold-start landing test. */
export function ColdStartLanding(): JSX.Element | null {
  const settingsOn = useServerFlag('settings_2_0_v1');
  const { pathname } = useLocation();
  const [armed, setArmed] = useState(() => pathname === '/');
  const done = useCallback(() => setArmed(false), []);

  useEffect(() => {
    if (armed && pathname !== '/' && pathname !== '/today') setArmed(false);
  }, [armed, pathname]);

  return armed && settingsOn && pathname === '/today' ? <ColdStartLandingTo onDone={done} /> : null;
}

export function App() {
  return (
    <BrowserRouter basename={routerBasename()}>
      {/* BOOT — the boot handoff bus (NativeRoot → BootVeil, read by TopBar).
          Renders only children (no DOM); inert on web (NativeRoot never
          mounts there), so web stays byte-identical. */}
      <BootProvider>
      {/* UX review 2026-09-08 — per-route <title>. Renders null; reads the
          pathname only, so no route file knows it exists. */}
      <DocumentTitle />
      <TypeRampRoot />
      {/* Phase 6 cross-cutting PR 1c-a — global mobile bottom-tab nav.
          Renders nothing outside Mobile tier (≤ 600px); self-gates via
          useMediaQuery + useAuthContext (signed-out users get no tab).
          Mounts here at App.tsx so it's available across every route
          including ones that bypass AppShell on Mobile (today, plan,
          chat, etc. all render their own mobile column). */}
      <BottomTab />
      {/* Persistent "Ask a coach" entry to /v2/chat (the coaches MOAT had no
          durable mobile entry — Today's CoachLine hides with no daily line and
          the bottom tabs have no Coaches item). Self-gates to ≤1023px + signed-in
          + non-chrome-free routes. Desktop uses the SideNav instead.
          2.0 chat (P9): with `chat_2_0_v1` on the gate renders nothing (the button and its tip are
          retired; the top-bar pill and the side-nav item are the doors); off, it renders CoachFab. */}
      <CoachFabGate />
      {/* B-3 — the draft-week landed ✓ chip. Self-gating: renders only when the
          draft takeover navigated here with a `draftChip` in location.state
          (§1g); inert on every ordinary navigation, no flag read needed. */}
      <DraftLandedChip />
      {/* BOOT §4 — the signed-in launch handoff veil (native-only; returns
          null on web). Mounted here, a sibling of the nav, so it survives the
          '/' → /today route change and reveals the day beneath as the mark
          docks into the TopBar. */}
      <BootVeil />
      {/* BOOT §3 — writes the last-foreground stamp on background for the whole
          session (native-only; renders null). Spans past NativeRoot's unmount
          so warm-relaunch tiering stays accurate. */}
      <BootActivityMount />
      {/* Native shell — intercept hardcoded /v2 anchors (web-absolute) and
          route them via the SPA router; a raw anchor click in the shell is a
          dead full-page load → white screen (founder 2026-07-09). Web: inert. */}
      <NativeLinkGuard />
      {/* Push tap-routing (push-notifications-v2.md §5.3) — routes a native
          notification TAP to its allowlisted in-app destination, queueing a
          cold-start tap until boot hands off. Renders null; self-gates to
          native + push_notifications_v1 (web / flag OFF = inert; the sw.js lane
          owns web taps). */}
      <PushTapMount />
      {/* H①-native — HealthKit daily-aggregate sync driver. Renders null;
          self-gates to native + signed-in + health_context_v1 (web / flag
          OFF = inert). Mounted here so it spans the whole native session. */}
      <HealthSyncMount />
      {/* P4 — HealthKit workout import. Renders null; self-gates to native +
          signed-in + hk_import_v1 + the per-device opt-in. Imports the trailing
          7 days of running workouts on foreground (web / flag OFF = inert). */}
      <WorkoutImportMount />
      {/* P5 — offline run-save queue drain. Renders null; self-gates to
          native + signed-in. Flushes queued saves on foreground / online /
          app-open so a dead-zone save never gets lost (web / no user = inert). */}
      <RunSaveQueueMount />
      {/* REL-24 — StoreKit entitlement reconcile. Renders null; self-gates to
          native + signed-in + storekit_v1 (web / flag OFF = inert). Reads
          current entitlements at launch and on foreground and forwards
          Transaction.updates, so a purchase made on another device, restored
          after a reinstall or approved through Ask to Buy lands without anyone
          opening Settings. The server re-verifies every transaction. */}
      <StoreKitSyncMount />
      {/* FIELD-TEST BUG 3 — consume an invite code stashed before sign-in.
          Renders null; self-gates to signed-in + a stashed code, so every
          ordinary launch is inert. Mounted here (not on a route) because
          OAuth returns to /v2/today on web and to wherever the shell was on
          native — neither knows about the invite. Forwards to the existing
          /team?join= confirmation; it never joins on the athlete's behalf. */}
      <JoinCodeMount />
      {/* B300 — saves the device timezone for every signed-in account on app
          open, so the server's athlete day frame resolves for everyone.
          Renders null; signed out = inert. */}
      <TimezoneStampMount />
      {/* Wave 2 ruling 10: a cold start opens the stored View's first tab.
          Renders null; inert unless settings_2_0_v1 is on and the router began
          at the app root (see ColdStartLanding). */}
      <ColdStartLanding />
      {/* B76C · 2e — the dual arrival banner. Self-gating: renders only for an
          account the crossing ARMED, only on Today, and only until dismissed.
          Inert on every ordinary launch. */}
      <DualArrivalBanner />
      {/* B76C · 2b — lane B's seam, filled. Wraps the routes so every boundary
          CTA in the tree can open the one membership sheet. */}
      <AthleteMembershipHost>
      <Routes>
        {/* B418 - the guarded pages sit under SignedOutHold (three groups, because the public
            routes between them stay out): on the web, a visitor with no saved session sees nothing
            until sign-in status is known, then the landing (with landing_2_0_v1 on, the hold
            itself redirects to the sign-in sheet, so the page's chunk is never fetched; the
            landing chunk is prefetched while it waits; the blank is capped at 4 s). A saved
            session, and the native shell, render the page's own loading UI exactly as before.
            Today's ?user= design preview needs no session, so it is never held. */}
        <Route element={<SignedOutHold publicParam="user" prefetch={prefetchLandingForHold} />}>
        <Route
          path="/today"
          element={
            <AthleteSurfaceGuard>
              <Today />
            </AthleteSurfaceGuard>
          }
        />
        </Route>
        <Route path="/onboarding" element={<Onboarding />} />
        {/* REL-24 (guideline 3.1.1) — `/landing` is the MARKETING page: it
            renders PricingSection, with our own price strings and an "Upgrade
            when ready" pitch. Four sign-out and route-guard paths still
            navigate to it WITHOUT a platform check (SideNav, OnboardingShell,
            FirstPlanShell, and every signed-out `<Navigate to="/landing">`),
            so inside the shell it was reachable. Redirecting the ROUTE fixes
            all of them at once and leaves those call sites untouched; native
            already has `/welcome` as its signed-out surface. Web is unchanged. */}
        <Route
          path="/landing"
          element={
            isNativeApp() ? (
              <Navigate to="/welcome" replace />
            ) : (
              <Suspense fallback={<LandingFallback />}>
                <Landing />
              </Suspense>
            )
          }
        />
        <Route element={<SignedOutHold prefetch={prefetchLandingForHold} />}>
        <Route
          path="/chat"
          element={
            <Suspense fallback={<div role="status">Loading chat…</div>}>
              <Chat />
            </Suspense>
          }
        />
        <Route
          path="/plan"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading season plan…</div>}>
                <PlanEntry />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/plan/season"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading season plan…</div>}>
                <SeasonEntry />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/plan/first-look"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading your first plan…</div>}>
                <FirstLook />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/plan/draft"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading your draft…</div>}>
                <PlanDraft />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/plan/week/:n"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading week…</div>}>
                <PlanWeek />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/plan/calendar"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading calendar…</div>}>
                <PlanCalendar />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<SettingsRouteFallback />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path="/races"
          element={
            <Suspense fallback={<RouteFallback text="Loading races…" />}>
              <Races />
            </Suspense>
          }
        />
        <Route
          path="/team"
          element={
            <Suspense fallback={<RouteFallback text="Loading team…" />}>
              <Team />
            </Suspense>
          }
        />
        <Route
          path="/team/kudos"
          element={
            <Suspense fallback={<div role="status">Loading kudos…</div>}>
              <Team />
            </Suspense>
          }
        />
        </Route>
        {/* Map lane step 2 — the coach race-day live map (CD 2026-08-05
            R31/R32/R34). DARK behind race_telemetry_r2: the route self-gates
            fail-closed via useServerFlagReady AND the §4.3 poll 404s while
            the server flag is off. Lazy so mapkit wiring never rides an
            ordinary chunk. */}
        <Route
          path="/race-live/:teamId/:meetId"
          element={
            <Suspense fallback={<div role="status">Loading map…</div>}>
              <RaceLiveMap />
            </Suspense>
          }
        />
        {/* B14 — the door that always opens TODAY'S meet (static segment
            outranks the :teamId/:meetId pattern in v6 route ranking). */}
        <Route
          path="/race-live/today"
          element={
            <Suspense fallback={<div role="status">Loading map…</div>}>
              <RaceLiveToday />
            </Suspense>
          }
        />
        <Route element={<SignedOutHold prefetch={prefetchLandingForHold} />}>
        <Route
          path="/admin"
          element={
            <Suspense fallback={<div role="status">Loading admin…</div>}>
              <Admin />
            </Suspense>
          }
        />
        <Route
          path="/admin/:tab"
          element={
            <Suspense fallback={<div role="status">Loading admin…</div>}>
              <Admin />
            </Suspense>
          }
        />
        <Route
          path="/approvals"
          element={
            <Suspense fallback={<div role="status">Loading approvals…</div>}>
              <Approvals />
            </Suspense>
          }
        />
        <Route
          path="/guardian"
          element={
            <Suspense fallback={<div role="status">Loading guardian view…</div>}>
              <GuardianHome />
            </Suspense>
          }
        />
        </Route>
        <Route path="/primitives" element={<Primitives />} />
        {/* Mobile v2 (§6b) — /run is registered on web too now: the
            deployed app gets the READ-ONLY runs history + replay review
            surface (RunSpike self-gates live tracking to the shell via
            canTrack=isNativeApp()). The maplibre-gl chunk stays lazy, so
            web bundles only pay for it on a /run visit. */}
        <Route
          path="/run"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading runs…</div>}>
                <RunSpike />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        {/* P3.3 (E4) — the route progress-compare screen (sibling of /run). */}
        <Route
          path="/run/route/:routeId"
          element={
            <AthleteSurfaceGuard>
              <Suspense fallback={<div role="status">Loading route…</div>}>
                <RouteCompare />
              </Suspense>
            </AthleteSurfaceGuard>
          }
        />
        {/* Part B — the shell's entry: branded boot splash while auth
            resolves, then /today (signed in) or /landing (the sign-in
            surface). Web '/' is unreachable here (Vercel rewrites it
            before the SPA), so this stays native-only by construction. */}
        {isNativeApp() && (
          <Route
            path="/"
            element={
              <Suspense fallback={null}>
                <NativeRoot />
              </Suspense>
            }
          />
        )}
        {/* Mobile v1 D5/D4 — the signed-out welcome the splash settles
            into. Fallback is null (not a loading row) so the boot →
            welcome hand-off stays one unbroken cream beat. */}
        {isNativeApp() && (
          <Route
            path="/welcome"
            element={
              <Suspense fallback={null}>
                <NativeWelcome />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </AthleteMembershipHost>
      </BootProvider>
    </BrowserRouter>
  );
}
